import { create } from "zustand";
import {
  searchExternalItems as searchExternalItemsAPI,
  listAlbums as listAlbumsAPI,
  listResidents as listResidentsAPI,
  listRosterSegments as listRosterSegmentsAPI,
  getAlbum as getAlbumAPI,
  resolveExternalData as resolveExternalDataAPI,
  listMedia as listMediaAPI,
  uploadMedia as uploadMediaAPI,
  type MediaListFile,
  type MediaListFolder,
  listPrivateDocs as listPrivateDocsAPI,
  listPrivateDocCategories as listPrivateDocCategoriesAPI,
  uploadPrivateDoc as uploadPrivateDocAPI,
  type PrivateDocCategory,
  type PrivateDocListItem,
} from "../api";
import { useAppStore } from "./app-store";
import {
  searchExternalItemsLocal,
  resolveExternalDataLocal,
  listMediaLocal,
  uploadMediaLocal,
  listPrivateDocsLocal,
  listPrivateDocCategoriesLocal,
  uploadPrivateDocLocal,
  getAlbumLocal,
} from "../api/adapters/local-mocks";
import { getSelectedBackend } from "../utils/easyblocks-config/config";
import { localBackend } from "../api/adapters/local-backend";

// ========== NEWS SEARCH STATE (for NewsPickerWidget) ==========
export interface NewsSearchState {
  query: string;
  debouncedQuery: string;
  items: Array<{ id: string; name?: string; title?: string; [key: string]: any }>;
  isLoading: boolean;
  error: string | null;
}

// ========== NEWS LATEST STATE (for NewsLatestRow) ==========
export interface NewsLatestState {
  items: Array<any>;
  isLoading: boolean;
  error: string | null;
}

// ========== EVENTS SEARCH STATE (for EventsPickerWidget) ==========
export interface EventsSearchState {
  query: string;
  debouncedQuery: string;
  items: Array<{ id: string; C_com_group_item_name?: string; name?: string; title?: string; page_title?: string; [key: string]: any }>;
  isLoading: boolean;
  error: string | null;
}

// ========== EVENTS LATEST STATE (for EventsRow) ==========
export interface EventsLatestState {
  items: Array<any>;
  isLoading: boolean;
  error: string | null;
}

// ========== ROSTER STATE (for RosterAccordion) ==========
export interface RosterState {
  items: Array<any>;
  isLoading: boolean;
  error: string | null;
}

export interface RosterSegmentFiltersState {
  items: Array<{
    id: string;
    item_id: number;
    key: string;
    label: string;
    item_name?: string;
    list_sort: number;
    C_com_group_item_id?: number;
  }>;
  loadedForDocumentId: string | null;
  isLoading: boolean;
  error: string | null;
}

// ========== RESIDENT DIRECTORY STATE ==========
export interface ResidentDirectoryState {
  items: Array<any>;
  isLoading: boolean;
  error: string | null;
}

// ========== ALBUMS STATE (for Carousel) ==========
export interface AlbumsListState {
  items: Array<{ id: string; item_id: number; name: string; [key: string]: any }>;
  isLoading: boolean;
  error: string | null;
}

export interface AlbumDetailState {
  albumId: string;
  album: Record<string, any> | null;
  photos: Array<{
    id: string;
    item_id: number;
    item_name?: string;
    attachment_filename?: string;
    url?: string;
    [key: string]: any;
  }>;
  isLoading: boolean;
  error: string | null;
}

// ========== MEDIA BROWSER STATE (for ImageUploadWidget) ==========
export interface MediaBrowserState {
  isModalOpen: boolean;
  path: string;
  query: string;
  debouncedQuery: string;
  folders: MediaListFolder[];
  files: MediaListFile[];
  isLoading: boolean;
  listError: string | null;
  isUploading: boolean;
  uploadError: string | null;
  manualUrl: string;
}

// ========== DOCUMENTS BROWSER STATE (for DocumentUploadWidget) ==========
export type DocumentsSortBy = "name" | "postedDate";
export type DocumentsSortDirection = "asc" | "desc";

export interface DocumentsBrowserState {
  isModalOpen: boolean;
  documentScope: "current" | "shared";
  categoryId: number;
  year: number;
  sortBy: DocumentsSortBy;
  sortDirection: DocumentsSortDirection;
  sharedScopeAvailable: boolean | null;
  categories: PrivateDocCategory[];
  allItems: PrivateDocListItem[];
  items: PrivateDocListItem[];
  hasLoadedItems: boolean;
  isLoading: boolean;
  listError: string | null;
  isUploading: boolean;
  uploadError: string | null;
  isLoadingCategories: boolean;
  categoriesError: string | null;
}

// ========== COMBINED STORE ==========
// Per-instance state: each widget/component gets its own isolated state by instanceId
export interface ExternalDataStore {
  // Keyed by instanceId (e.g., "news-picker-0", "news-latest-0", "media-browser-0")
  newsSearch: Record<string, NewsSearchState>;
  newsLatest: Record<string, NewsLatestState>;
  eventsSearch: Record<string, EventsSearchState>;
  eventsLatest: Record<string, EventsLatestState>;
  roster: Record<string, RosterState>;
  rosterSegmentFilters: RosterSegmentFiltersState;
  residentDirectory: Record<string, ResidentDirectoryState>;
  albumsList: Record<string, AlbumsListState>;
  albumDetail: Record<string, AlbumDetailState>;
  mediaBrowser: Record<string, MediaBrowserState>;
  documentsBrowser: Record<string, DocumentsBrowserState>;

  // News Search Actions
  setNewsSearchQuery: (instanceId: string, query: string) => void;
  setNewsSearchDebouncedQuery: (instanceId: string, debouncedQuery: string) => void;
  searchNews: (instanceId: string, query: string, limit?: number) => Promise<void>;
  resetNewsSearch: (instanceId: string) => void;

  // News Latest Actions
  ensureNewsLatest: (instanceId: string) => void;
  loadLatestNews: (instanceId: string, limit?: number) => Promise<void>;
  resetNewsLatest: (instanceId: string) => void;

  // Events Search Actions
  setEventsSearchQuery: (instanceId: string, query: string) => void;
  setEventsSearchDebouncedQuery: (instanceId: string, debouncedQuery: string) => void;
  searchEvents: (instanceId: string, query: string, limit?: number) => Promise<void>;
  resetEventsSearch: (instanceId: string) => void;

  // Events Latest Actions
  ensureEventsLatest: (instanceId: string) => void;
  loadLatestEvents: (instanceId: string, limit?: number) => Promise<void>;
  resetEventsLatest: (instanceId: string) => void;

  // Roster Actions
  ensureRoster: (instanceId: string) => void;
  loadRoster: (instanceId: string, limit?: number) => Promise<void>;
  resetRoster: (instanceId: string) => void;
  loadRosterSegmentFilters: (documentId?: string | null) => Promise<void>;
  resetRosterSegmentFilters: () => void;

  // Resident Directory Actions
  ensureResidentDirectory: (instanceId: string) => void;
  loadResidentDirectory: (instanceId: string, limit?: number) => Promise<void>;
  resetResidentDirectory: (instanceId: string) => void;

  // Albums Actions
  ensureAlbumsList: (instanceId: string) => void;
  listAlbums: (
    instanceId: string,
    limit?: number,
    options?: { includeGlobalAlbums?: boolean }
  ) => Promise<void>;
  getAlbum: (
    instanceId: string,
    albumId: string,
    options?: { includeGlobalAlbums?: boolean }
  ) => Promise<void>;
  resetAlbums: (instanceId: string) => void;

  // Media Browser Actions
  setMediaBrowserModalOpen: (instanceId: string, isOpen: boolean) => void;
  setMediaBrowserPath: (instanceId: string, path: string) => void;
  setMediaBrowserQuery: (instanceId: string, query: string) => void;
  setMediaBrowserDebouncedQuery: (instanceId: string, debouncedQuery: string) => void;
  setMediaBrowserManualUrl: (instanceId: string, url: string) => void;
  listMedia: (instanceId: string, path: string, query: string) => Promise<void>;
  uploadMedia: (
    instanceId: string,
    file: File,
    path?: string,
    uploadContext?: string
  ) => Promise<string>;
  resetMediaBrowser: (instanceId: string) => void;

  // Documents Browser Actions
  setDocumentsBrowserModalOpen: (instanceId: string, isOpen: boolean) => void;
  setDocumentsBrowserScope: (instanceId: string, scope: "current" | "shared") => void;
  setDocumentsBrowserCategoryId: (instanceId: string, categoryId: number) => void;
  setDocumentsBrowserYear: (instanceId: string, year: number) => void;
  setDocumentsBrowserSortBy: (instanceId: string, sortBy: DocumentsSortBy) => void;
  setDocumentsBrowserSortDirection: (instanceId: string, sortDirection: DocumentsSortDirection) => void;
  listDocuments: (
    instanceId: string,
    categoryId: number,
    year?: number,
    documentScope?: "current" | "shared"
  ) => Promise<void>;
  listDocumentCategories: (
    instanceId: string,
    documentScope?: "current" | "shared"
  ) => Promise<void>;
  uploadDocument: (
    instanceId: string,
    file: File,
    categoryId: number,
    itemName?: string
  ) => Promise<PrivateDocListItem>;
  resetDocumentsBrowser: (instanceId: string) => void;
}

// Default states
const defaultNewsSearchState = (): NewsSearchState => ({
  query: "",
  debouncedQuery: "",
  items: [],
  isLoading: false,
  error: null,
});

const defaultNewsLatestState = (): NewsLatestState => ({
  items: [],
  isLoading: false,
  error: null,
});

const defaultEventsSearchState = (): EventsSearchState => ({
  query: "",
  debouncedQuery: "",
  items: [],
  isLoading: false,
  error: null,
});

const defaultEventsLatestState = (): EventsLatestState => ({
  items: [],
  isLoading: false,
  error: null,
});

const defaultRosterState = (): RosterState => ({
  items: [],
  isLoading: false,
  error: null,
});

const defaultRosterSegmentFiltersState = (): RosterSegmentFiltersState => ({
  items: [],
  loadedForDocumentId: null,
  isLoading: false,
  error: null,
});

const defaultResidentDirectoryState = (): ResidentDirectoryState => ({
  items: [],
  isLoading: false,
  error: null,
});

const defaultAlbumsListState = (): AlbumsListState => ({
  items: [],
  isLoading: false,
  error: null,
});

const defaultAlbumDetailState = (): AlbumDetailState => ({
  albumId: "",
  album: null,
  photos: [],
  isLoading: false,
  error: null,
});

const defaultMediaBrowserState = (): MediaBrowserState => ({
  isModalOpen: false,
  path: "",
  query: "",
  debouncedQuery: "",
  folders: [],
  files: [],
  isLoading: false,
  listError: null,
  isUploading: false,
  uploadError: null,
  manualUrl: "",
});

const defaultDocumentsBrowserState = (): DocumentsBrowserState => ({
  isModalOpen: false,
  documentScope: "current",
  categoryId: 0,
  year: 0,
  sortBy: "postedDate",
  sortDirection: "desc",
  sharedScopeAvailable: null,
  categories: [],
  allItems: [],
  items: [],
  hasLoadedItems: false,
  isLoading: false,
  listError: null,
  isUploading: false,
  uploadError: null,
  isLoadingCategories: false,
  categoriesError: null,
});

function parsePostedDateForSort(value?: string): number {
  if (!value) {
    return 0;
  }

  const trimmed = value.trim();
  const parts = trimmed.split("/");
  if (parts.length === 3) {
    const month = Number(parts[0]);
    const day = Number(parts[1]);
    const year = Number(parts[2]);
    if (month > 0 && day > 0 && year > 0) {
      return Date.UTC(year, month - 1, day);
    }
  }

  const parsed = Date.parse(trimmed);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function sortPrivateDocItems(
  items: PrivateDocListItem[],
  sortBy: DocumentsSortBy,
  sortDirection: DocumentsSortDirection = "desc"
): PrivateDocListItem[] {
  const sorted = [...items];
  const directionMultiplier = sortDirection === "asc" ? 1 : -1;

  if (sortBy === "name") {
    sorted.sort((a, b) => {
      const nameA = (a.name || a.filename || "").trim().toLocaleLowerCase();
      const nameB = (b.name || b.filename || "").trim().toLocaleLowerCase();
      return directionMultiplier * nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
    });
    return sorted;
  }

  sorted.sort((a, b) => {
    const dateA = parsePostedDateForSort(a.publishDate);
    const dateB = parsePostedDateForSort(b.publishDate);
    if (dateA !== dateB) {
      return directionMultiplier * (dateA - dateB);
    }

    const nameA = (a.name || a.filename || "").trim().toLocaleLowerCase();
    const nameB = (b.name || b.filename || "").trim().toLocaleLowerCase();
    return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
  });

  return sorted;
}

function filterPrivateDocItems(
  items: PrivateDocListItem[],
  categoryId: number,
  year: number,
  sortBy: DocumentsSortBy = "postedDate",
  sortDirection: DocumentsSortDirection = "desc"
): PrivateDocListItem[] {
  const filtered = items.filter((item) => {
    if (categoryId > 0 && Number(item.categoryId ?? 0) !== categoryId) {
      return false;
    }
    if (year > 0 && Number(item.year ?? 0) !== year) {
      return false;
    }
    return true;
  });

  return sortPrivateDocItems(filtered, sortBy, sortDirection);
}

function getFilteredDocumentsBrowserItems(instance: DocumentsBrowserState): PrivateDocListItem[] {
  return filterPrivateDocItems(
    instance.allItems,
    instance.categoryId,
    instance.year,
    instance.sortBy,
    instance.sortDirection
  );
}

// Helper to get or create instance state
function getOrCreateNewsSearch(state: ExternalDataStore, instanceId: string): NewsSearchState {
  if (!state.newsSearch[instanceId]) {
    state.newsSearch[instanceId] = defaultNewsSearchState();
  }
  return state.newsSearch[instanceId];
}

function getOrCreateNewsLatest(state: ExternalDataStore, instanceId: string): NewsLatestState {
  if (!state.newsLatest[instanceId]) {
    state.newsLatest[instanceId] = defaultNewsLatestState();
  }
  return state.newsLatest[instanceId];
}

function getOrCreateEventsSearch(state: ExternalDataStore, instanceId: string): EventsSearchState {
  if (!state.eventsSearch[instanceId]) {
    state.eventsSearch[instanceId] = defaultEventsSearchState();
  }
  return state.eventsSearch[instanceId];
}

function getOrCreateEventsLatest(state: ExternalDataStore, instanceId: string): EventsLatestState {
  if (!state.eventsLatest[instanceId]) {
    state.eventsLatest[instanceId] = defaultEventsLatestState();
  }
  return state.eventsLatest[instanceId];
}

function getOrCreateRoster(state: ExternalDataStore, instanceId: string): RosterState {
  if (!state.roster[instanceId]) {
    state.roster[instanceId] = defaultRosterState();
  }
  return state.roster[instanceId];
}

function getOrCreateResidentDirectory(state: ExternalDataStore, instanceId: string): ResidentDirectoryState {
  if (!state.residentDirectory[instanceId]) {
    state.residentDirectory[instanceId] = defaultResidentDirectoryState();
  }
  return state.residentDirectory[instanceId];
}

function getOrCreateAlbumsList(state: ExternalDataStore, instanceId: string): AlbumsListState {
  if (!state.albumsList[instanceId]) {
    state.albumsList[instanceId] = defaultAlbumsListState();
  }
  return state.albumsList[instanceId];
}

function getOrCreateAlbumDetail(state: ExternalDataStore, instanceId: string): AlbumDetailState {
  if (!state.albumDetail[instanceId]) {
    state.albumDetail[instanceId] = defaultAlbumDetailState();
  }
  return state.albumDetail[instanceId];
}

function getOrCreateMediaBrowser(state: ExternalDataStore, instanceId: string): MediaBrowserState {
  if (!state.mediaBrowser[instanceId]) {
    state.mediaBrowser[instanceId] = defaultMediaBrowserState();
  }
  return state.mediaBrowser[instanceId];
}

function getOrCreateDocumentsBrowser(state: ExternalDataStore, instanceId: string): DocumentsBrowserState {
  if (!state.documentsBrowser[instanceId]) {
    state.documentsBrowser[instanceId] = defaultDocumentsBrowserState();
  }
  return state.documentsBrowser[instanceId];
}

export const useExternalDataStore = create<ExternalDataStore>((set, get) => ({
  newsSearch: {},
  newsLatest: {},
  eventsSearch: {},
  eventsLatest: {},
  roster: {},
  rosterSegmentFilters: defaultRosterSegmentFiltersState(),
  residentDirectory: {},
  albumsList: {},
  albumDetail: {},
  mediaBrowser: {},
  documentsBrowser: {},

  // ========== NEWS SEARCH ACTIONS ==========
  setNewsSearchQuery: (instanceId, query) =>
    set((state) => {
      const instance = getOrCreateNewsSearch(state, instanceId);
      return {
        newsSearch: {
          ...state.newsSearch,
          [instanceId]: { ...instance, query },
        },
      };
    }),

  setNewsSearchDebouncedQuery: (instanceId, debouncedQuery) =>
    set((state) => {
      const instance = getOrCreateNewsSearch(state, instanceId);
      return {
        newsSearch: {
          ...state.newsSearch,
          [instanceId]: { ...instance, debouncedQuery },
        },
      };
    }),

  searchNews: async (instanceId, query, limit = 50) => {
    const state = get();
    const instance = getOrCreateNewsSearch(state, instanceId);

    set({
      newsSearch: {
        ...state.newsSearch,
        [instanceId]: { ...instance, isLoading: true, error: null },
      },
    });

    try {
      // Check if using local backend
      const isLocal = getSelectedBackend() === localBackend;
      const documentId = useAppStore.getState().documentId;
      const res = isLocal
        ? searchExternalItemsLocal({ source: "news", q: query, limit })
        : await searchExternalItemsAPI({
            source: "news",
            q: query,
            limit,
            document_id: documentId ?? undefined,
          });

      const currentState = get();
      const currentInstance = currentState.newsSearch[instanceId];
      if (currentInstance) {
        set({
          newsSearch: {
            ...currentState.newsSearch,
            [instanceId]: {
              ...currentInstance,
              items: (res.items ?? []) as any[],
              isLoading: false,
            },
          },
        });
      }
    } catch (e: any) {
      const currentState = get();
      const currentInstance = currentState.newsSearch[instanceId];
      if (currentInstance) {
        set({
          newsSearch: {
            ...currentState.newsSearch,
            [instanceId]: {
              ...currentInstance,
              items: [],
              error: e?.message ?? "Search failed",
              isLoading: false,
            },
          },
        });
      }
    }
  },

  resetNewsSearch: (instanceId) =>
    set((state) => {
      const newState = { ...state.newsSearch };
      delete newState[instanceId];
      return { newsSearch: newState };
    }),

  // ========== NEWS LATEST ACTIONS ==========
  ensureNewsLatest: (instanceId) =>
    set((state) => {
      if (state.newsLatest[instanceId]) return state;
      const instance = getOrCreateNewsLatest(state, instanceId);
      return {
        newsLatest: {
          ...state.newsLatest,
          [instanceId]: { ...instance },
        },
      };
    }),
  loadLatestNews: async (instanceId, limit = 3) => {
    const state = get();
    const instance = getOrCreateNewsLatest(state, instanceId);

    set({
      newsLatest: {
        ...state.newsLatest,
        [instanceId]: { ...instance, isLoading: true, error: null },
      },
    });

    try {
      // Check if using local backend
      const isLocal = getSelectedBackend() === localBackend;
      const documentId = useAppStore.getState().documentId;
      // Search for recent items
      const search = isLocal
        ? searchExternalItemsLocal({ source: "news", q: "", limit })
        : await searchExternalItemsAPI({
            source: "news",
            q: "",
            limit,
            document_id: documentId ?? undefined,
          });

      const ids = (search.items ?? [])
        .map((it: any) => String(it?.id ?? ""))
        .filter((id) => id.length > 0)
        .slice(0, limit);

      // Resolve full items
      const externals = Object.fromEntries(
        ids.map((id, idx) => [`NewsLatestRow.${instanceId}.${idx}`, { id, widgetId: "news", type: "news" }])
      );

      const resolved = isLocal
        ? resolveExternalDataLocal({ externals })
        : await resolveExternalDataAPI({ externals, document_id: documentId ?? undefined });

      const items: any[] = ids
        .map((id, idx) => {
          const key = `NewsLatestRow.${instanceId}.${idx}`;
          const entry: any = (resolved.externalData as any)?.[key];
          const value: any = entry?.value;
          if (!value) return null;
          return { ...value, __id: id };
        })
        .filter(Boolean);

      const currentState = get();
      const currentInstance = currentState.newsLatest[instanceId];
      if (currentInstance) {
        set({
          newsLatest: {
            ...currentState.newsLatest,
            [instanceId]: {
              ...currentInstance,
              items,
              isLoading: false,
            },
          },
        });
      }
    } catch (e: any) {
      const currentState = get();
      const currentInstance = currentState.newsLatest[instanceId];
      if (currentInstance) {
        set({
          newsLatest: {
            ...currentState.newsLatest,
            [instanceId]: {
              ...currentInstance,
              items: [],
              error: e?.message ?? "Failed to load latest news",
              isLoading: false,
            },
          },
        });
      }
    }
  },

  resetNewsLatest: (instanceId) =>
    set((state) => {
      const newState = { ...state.newsLatest };
      delete newState[instanceId];
      return { newsLatest: newState };
    }),

  // ========== EVENTS SEARCH ACTIONS ==========
  setEventsSearchQuery: (instanceId, query) =>
    set((state) => {
      const instance = getOrCreateEventsSearch(state, instanceId);
      return {
        eventsSearch: {
          ...state.eventsSearch,
          [instanceId]: { ...instance, query },
        },
      };
    }),

  setEventsSearchDebouncedQuery: (instanceId, debouncedQuery) =>
    set((state) => {
      const instance = getOrCreateEventsSearch(state, instanceId);
      return {
        eventsSearch: {
          ...state.eventsSearch,
          [instanceId]: { ...instance, debouncedQuery },
        },
      };
    }),

  searchEvents: async (instanceId, query, limit = 50) => {
    const state = get();
    const instance = getOrCreateEventsSearch(state, instanceId);

    set({
      eventsSearch: {
        ...state.eventsSearch,
        [instanceId]: { ...instance, isLoading: true, error: null },
      },
    });

    try {
      // Check if using local backend
      const isLocal = getSelectedBackend() === localBackend;
      const documentId = useAppStore.getState().documentId;
      const res = isLocal
        ? searchExternalItemsLocal({ source: "events", q: query, limit })
        : await searchExternalItemsAPI({
            source: "events",
            q: query,
            limit,
            document_id: documentId ?? undefined,
          });

      const currentState = get();
      const currentInstance = currentState.eventsSearch[instanceId];
      if (currentInstance) {
        set({
          eventsSearch: {
            ...currentState.eventsSearch,
            [instanceId]: {
              ...currentInstance,
              items: (res.items ?? []) as any[],
              isLoading: false,
            },
          },
        });
      }
    } catch (e: any) {
      const currentState = get();
      const currentInstance = currentState.eventsSearch[instanceId];
      if (currentInstance) {
        set({
          eventsSearch: {
            ...currentState.eventsSearch,
            [instanceId]: {
              ...currentInstance,
              items: [],
              error: e?.message ?? "Search failed",
              isLoading: false,
            },
          },
        });
      }
    }
  },

  resetEventsSearch: (instanceId) =>
    set((state) => {
      const newState = { ...state.eventsSearch };
      delete newState[instanceId];
      return { eventsSearch: newState };
    }),

  // ========== EVENTS LATEST ACTIONS ==========
  ensureEventsLatest: (instanceId) =>
    set((state) => {
      if (state.eventsLatest[instanceId]) return state;
      const instance = getOrCreateEventsLatest(state, instanceId);
      return {
        eventsLatest: {
          ...state.eventsLatest,
          [instanceId]: { ...instance },
        },
      };
    }),
  loadLatestEvents: async (instanceId, limit = 3) => {
    const state = get();
    const instance = getOrCreateEventsLatest(state, instanceId);

    set({
      eventsLatest: {
        ...state.eventsLatest,
        [instanceId]: { ...instance, isLoading: true, error: null },
      },
    });

    try {
      // Check if using local backend
      const isLocal = getSelectedBackend() === localBackend;
      const documentId = useAppStore.getState().documentId;
      // Search for recent items
      const search = isLocal
        ? searchExternalItemsLocal({ source: "events", q: "", limit })
        : await searchExternalItemsAPI({
            source: "events",
            q: "",
            limit,
            document_id: documentId ?? undefined,
          });

      const ids = (search.items ?? [])
        .map((it: any) => String(it?.id ?? ""))
        .filter((id) => id.length > 0)
        .slice(0, limit);

      // Resolve full items
      const externals = Object.fromEntries(
        ids.map((id, idx) => [`EventsRow.${instanceId}.${idx}`, { id, widgetId: "events", type: "events" }])
      );

      const resolved = isLocal
        ? resolveExternalDataLocal({ externals })
        : await resolveExternalDataAPI({ externals, document_id: documentId ?? undefined });

      const items: any[] = ids
        .map((id, idx) => {
          const key = `EventsRow.${instanceId}.${idx}`;
          const entry: any = (resolved.externalData as any)?.[key];
          const value: any = entry?.value;
          if (!value) return null;
          return { ...value, __id: id };
        })
        .filter(Boolean);

      const currentState = get();
      const currentInstance = currentState.eventsLatest[instanceId];
      if (currentInstance) {
        set({
          eventsLatest: {
            ...currentState.eventsLatest,
            [instanceId]: {
              ...currentInstance,
              items,
              isLoading: false,
            },
          },
        });
      }
    } catch (e: any) {
      const currentState = get();
      const currentInstance = currentState.eventsLatest[instanceId];
      if (currentInstance) {
        set({
          eventsLatest: {
            ...currentState.eventsLatest,
            [instanceId]: {
              ...currentInstance,
              items: [],
              error: e?.message ?? "Failed to load latest events",
              isLoading: false,
            },
          },
        });
      }
    }
  },

  resetEventsLatest: (instanceId) =>
    set((state) => {
      const newState = { ...state.eventsLatest };
      delete newState[instanceId];
      return { eventsLatest: newState };
    }),

  // ========== ROSTER ACTIONS ==========
  ensureRoster: (instanceId) =>
    set((state) => {
      if (state.roster[instanceId]) return state;
      const instance = getOrCreateRoster(state, instanceId);
      return {
        roster: {
          ...state.roster,
          [instanceId]: { ...instance },
        },
      };
    }),
  loadRoster: async (instanceId, limit = 200) => {
    const state = get();
    const instance = getOrCreateRoster(state, instanceId);

    set({
      roster: {
        ...state.roster,
        [instanceId]: { ...instance, isLoading: true, error: null },
      },
    });

    try {
      const isLocal = getSelectedBackend() === localBackend;
      const documentId = useAppStore.getState().documentId;
      const res = isLocal
        ? searchExternalItemsLocal({ source: "roster", q: "", limit })
        : await searchExternalItemsAPI({
            source: "roster",
            q: "",
            limit,
            document_id: documentId ?? undefined,
          });

      const currentState = get();
      const currentInstance = currentState.roster[instanceId];
      if (currentInstance) {
        set({
          roster: {
            ...currentState.roster,
            [instanceId]: {
              ...currentInstance,
              items: (res.items ?? []) as any[],
              isLoading: false,
            },
          },
        });
      }
    } catch (e: any) {
      const currentState = get();
      const currentInstance = currentState.roster[instanceId];
      if (currentInstance) {
        set({
          roster: {
            ...currentState.roster,
            [instanceId]: {
              ...currentInstance,
              items: [],
              error: e?.message ?? "Failed to load roster",
              isLoading: false,
            },
          },
        });
      }
    }
  },
  resetRoster: (instanceId) =>
    set((state) => {
      const newState = { ...state.roster };
      delete newState[instanceId];
      return { roster: newState };
    }),
  loadRosterSegmentFilters: async (documentId) => {
    const docId = String(documentId ?? useAppStore.getState().documentId ?? "").trim();
    if (docId === "") {
      set({ rosterSegmentFilters: defaultRosterSegmentFiltersState() });
      return;
    }

    const currentState = get();
    const currentFilters = currentState.rosterSegmentFilters;
    if (
      currentFilters.loadedForDocumentId === docId &&
      (currentFilters.isLoading || (currentFilters.items.length > 0 && !currentFilters.error))
    ) {
      return;
    }

    set({
      rosterSegmentFilters: {
        items: [],
        loadedForDocumentId: docId,
        isLoading: true,
        error: null,
      },
    });

    try {
      const isLocal = getSelectedBackend() === localBackend;
      const items = isLocal
        ? (searchExternalItemsLocal({ source: "roster_segments", q: "", limit: 20 }).items ?? [])
        : await listRosterSegmentsAPI(docId, 20);

      set({
        rosterSegmentFilters: {
          items: [...(items ?? [])].sort((a: any, b: any) => Number(a?.list_sort ?? 0) - Number(b?.list_sort ?? 0)),
          loadedForDocumentId: docId,
          isLoading: false,
          error: null,
        },
      });
    } catch (e: any) {
      set({
        rosterSegmentFilters: {
          items: [],
          loadedForDocumentId: docId,
          isLoading: false,
          error: e?.message ?? "Failed to load roster segments",
        },
      });
    }
  },
  resetRosterSegmentFilters: () =>
    set({
      rosterSegmentFilters: defaultRosterSegmentFiltersState(),
    }),

  // ========== RESIDENT DIRECTORY ACTIONS ==========
  ensureResidentDirectory: (instanceId) =>
    set((state) => {
      if (state.residentDirectory[instanceId]) return state;
      const instance = getOrCreateResidentDirectory(state, instanceId);
      return {
        residentDirectory: {
          ...state.residentDirectory,
          [instanceId]: { ...instance },
        },
      };
    }),
  loadResidentDirectory: async (instanceId, limit = 5000) => {
    const state = get();
    const instance = getOrCreateResidentDirectory(state, instanceId);

    set({
      residentDirectory: {
        ...state.residentDirectory,
        [instanceId]: { ...instance, isLoading: true, error: null },
      },
    });

    try {
      const isLocal = getSelectedBackend() === localBackend;
      const documentId = useAppStore.getState().documentId;
      const items = isLocal
        ? (searchExternalItemsLocal({ source: "residents", q: "", limit }).items ?? [])
        : await listResidentsAPI(documentId ?? undefined, limit);

      const currentState = get();
      const currentInstance = currentState.residentDirectory[instanceId];
      if (currentInstance) {
        set({
          residentDirectory: {
            ...currentState.residentDirectory,
            [instanceId]: {
              ...currentInstance,
              items: items as any[],
              isLoading: false,
              error: null,
            },
          },
        });
      }
    } catch (e: any) {
      const currentState = get();
      const currentInstance = currentState.residentDirectory[instanceId];
      if (currentInstance) {
        set({
          residentDirectory: {
            ...currentState.residentDirectory,
            [instanceId]: {
              ...currentInstance,
              items: [],
              error: e?.message ?? "Failed to load resident directory",
              isLoading: false,
            },
          },
        });
      }
    }
  },
  resetResidentDirectory: (instanceId) =>
    set((state) => {
      const newState = { ...state.residentDirectory };
      delete newState[instanceId];
      return { residentDirectory: newState };
    }),

  // ========== ALBUMS ACTIONS ==========
  ensureAlbumsList: (instanceId) =>
    set((state) => {
      if (state.albumsList[instanceId]) return state;
      const instance = getOrCreateAlbumsList(state, instanceId);
      return {
        albumsList: {
          ...state.albumsList,
          [instanceId]: { ...instance },
        },
      };
    }),
  listAlbums: async (instanceId, limit = 200, options) => {
    const state = get();
    const listInstance = getOrCreateAlbumsList(state, instanceId);

    set({
      albumsList: {
        ...state.albumsList,
        [instanceId]: { ...listInstance, isLoading: true, error: null },
      },
    });

    try {
      const isLocal = getSelectedBackend() === localBackend;
      const documentId = useAppStore.getState().documentId;
      const items = isLocal
        ? (searchExternalItemsLocal({ source: "albums", q: "", limit }).items ?? [])
        : await listAlbumsAPI(documentId ?? undefined, limit, options);

      const currentState = get();
      const currentInstance = currentState.albumsList[instanceId];
      if (currentInstance) {
        set({
          albumsList: {
            ...currentState.albumsList,
            [instanceId]: {
              ...currentInstance,
              items: (items ?? []) as any[],
              isLoading: false,
              error: null,
            },
          },
        });
      }
    } catch (e: any) {
      const currentState = get();
      const currentInstance = currentState.albumsList[instanceId];
      if (currentInstance) {
        set({
          albumsList: {
            ...currentState.albumsList,
            [instanceId]: {
              ...currentInstance,
              items: [],
              error: e?.message ?? "Failed to load albums",
              isLoading: false,
            },
          },
        });
      }
    }
  },
  getAlbum: async (instanceId, albumId, options) => {
    const state = get();
    const detailInstance = getOrCreateAlbumDetail(state, instanceId);

    set({
      albumDetail: {
        ...state.albumDetail,
        [instanceId]: {
          ...detailInstance,
          albumId,
          isLoading: true,
          error: null,
        },
      },
    });

    try {
      const isLocal = getSelectedBackend() === localBackend;
      const documentId = useAppStore.getState().documentId;
      const detail = isLocal
        ? getAlbumLocal(albumId)
        : await getAlbumAPI(albumId, documentId ?? undefined, options);

      const currentState = get();
      const currentInstance = currentState.albumDetail[instanceId];
      if (currentInstance) {
        set({
          albumDetail: {
            ...currentState.albumDetail,
            [instanceId]: {
              ...currentInstance,
              albumId,
              album: detail.album ?? null,
              photos: (detail.photos ?? []) as any[],
              isLoading: false,
              error: null,
            },
          },
        });
      }
    } catch (e: any) {
      const currentState = get();
      const currentInstance = currentState.albumDetail[instanceId];
      if (currentInstance) {
        set({
          albumDetail: {
            ...currentState.albumDetail,
            [instanceId]: {
              ...currentInstance,
              albumId,
              album: null,
              photos: [],
              error: e?.message ?? "Failed to load album",
              isLoading: false,
            },
          },
        });
      }
    }
  },
  resetAlbums: (instanceId) =>
    set((state) => {
      const newList = { ...state.albumsList };
      const newDetail = { ...state.albumDetail };
      delete newList[instanceId];
      delete newDetail[instanceId];
      return {
        albumsList: newList,
        albumDetail: newDetail,
      };
    }),

  // ========== MEDIA BROWSER ACTIONS ==========
  setMediaBrowserModalOpen: (instanceId, isOpen) =>
    set((state) => {
      const instance = getOrCreateMediaBrowser(state, instanceId);
      return {
        mediaBrowser: {
          ...state.mediaBrowser,
          [instanceId]: { ...instance, isModalOpen: isOpen },
        },
      };
    }),

  setMediaBrowserPath: (instanceId, path) =>
    set((state) => {
      const instance = getOrCreateMediaBrowser(state, instanceId);
      return {
        mediaBrowser: {
          ...state.mediaBrowser,
          [instanceId]: { ...instance, path },
        },
      };
    }),

  setMediaBrowserQuery: (instanceId, query) =>
    set((state) => {
      const instance = getOrCreateMediaBrowser(state, instanceId);
      return {
        mediaBrowser: {
          ...state.mediaBrowser,
          [instanceId]: { ...instance, query },
        },
      };
    }),

  setMediaBrowserDebouncedQuery: (instanceId, debouncedQuery) =>
    set((state) => {
      const instance = getOrCreateMediaBrowser(state, instanceId);
      return {
        mediaBrowser: {
          ...state.mediaBrowser,
          [instanceId]: { ...instance, debouncedQuery },
        },
      };
    }),

  setMediaBrowserManualUrl: (instanceId, url) =>
    set((state) => {
      const instance = getOrCreateMediaBrowser(state, instanceId);
      return {
        mediaBrowser: {
          ...state.mediaBrowser,
          [instanceId]: { ...instance, manualUrl: url },
        },
      };
    }),

  listMedia: async (instanceId, path, query) => {
    const state = get();
    const instance = getOrCreateMediaBrowser(state, instanceId);

    set({
      mediaBrowser: {
        ...state.mediaBrowser,
        [instanceId]: { ...instance, isLoading: true, listError: null },
      },
    });

    try {
      const isLocal = getSelectedBackend() === localBackend;
      const res = isLocal
        ? listMediaLocal({ path, q: query })
        : await listMediaAPI({ path, q: query });

      const currentState = get();
      const currentInstance = currentState.mediaBrowser[instanceId];
      if (currentInstance) {
        set({
          mediaBrowser: {
            ...currentState.mediaBrowser,
            [instanceId]: {
              ...currentInstance,
              path: String((res as any)?.path ?? path ?? ""),
              folders: res.folders ?? [],
              files: res.files ?? [],
              isLoading: false,
            },
          },
        });
      }
    } catch (e: any) {
      const currentState = get();
      const currentInstance = currentState.mediaBrowser[instanceId];
      if (currentInstance) {
        set({
          mediaBrowser: {
            ...currentState.mediaBrowser,
            [instanceId]: {
              ...currentInstance,
              folders: [],
              files: [],
              listError: e?.message ?? "Failed to load media",
              isLoading: false,
            },
          },
        });
      }
    }
  },

  uploadMedia: async (instanceId, file, path, uploadContext) => {
    const state = get();
    const instance = getOrCreateMediaBrowser(state, instanceId);

    set({
      mediaBrowser: {
        ...state.mediaBrowser,
        [instanceId]: { ...instance, isUploading: true, uploadError: null },
      },
    });

    try {
      const isLocal = getSelectedBackend() === localBackend;
      const res = isLocal
        ? await uploadMediaLocal(file, path, uploadContext)
        : await uploadMediaAPI(file, path, uploadContext);

      const url = String(res?.url ?? "").trim();
      if (!url) throw new Error("Upload succeeded but no URL returned");

      const currentState = get();
      const currentInstance = currentState.mediaBrowser[instanceId];
      if (currentInstance) {
        set({
          mediaBrowser: {
            ...currentState.mediaBrowser,
            [instanceId]: {
              ...currentInstance,
              isUploading: false,
              uploadError: null,
            },
          },
        });
      }
      return url;
    } catch (e: any) {
      const currentState = get();
      const currentInstance = currentState.mediaBrowser[instanceId];
      if (currentInstance) {
        set({
          mediaBrowser: {
            ...currentState.mediaBrowser,
            [instanceId]: {
              ...currentInstance,
              isUploading: false,
              uploadError: "Upload failed. Please try again.",
            },
          },
        });
      }
      throw e;
    }
  },

  resetMediaBrowser: (instanceId) =>
    set((state) => {
      const newState = { ...state.mediaBrowser };
      delete newState[instanceId];
      return { mediaBrowser: newState };
    }),

  // ========== DOCUMENTS BROWSER ACTIONS ==========
  setDocumentsBrowserModalOpen: (instanceId, isOpen) =>
    set((state) => {
      const instance = getOrCreateDocumentsBrowser(state, instanceId);
      return {
        documentsBrowser: {
          ...state.documentsBrowser,
          [instanceId]: { ...instance, isModalOpen: isOpen },
        },
      };
    }),

  setDocumentsBrowserScope: (instanceId, documentScope) =>
    set((state) => {
      const instance = getOrCreateDocumentsBrowser(state, instanceId);
      return {
        documentsBrowser: {
          ...state.documentsBrowser,
          [instanceId]: {
            ...instance,
            documentScope,
            categoryId: 0,
            categories: [],
            allItems: [],
            items: [],
            hasLoadedItems: false,
            listError: null,
            categoriesError: null,
          },
        },
      };
    }),

  setDocumentsBrowserCategoryId: (instanceId, categoryId) =>
    set((state) => {
      const instance = getOrCreateDocumentsBrowser(state, instanceId);
      return {
        documentsBrowser: {
          ...state.documentsBrowser,
          [instanceId]: { ...instance, categoryId },
        },
      };
    }),

  setDocumentsBrowserYear: (instanceId, year) =>
    set((state) => {
      const instance = getOrCreateDocumentsBrowser(state, instanceId);
      return {
        documentsBrowser: {
          ...state.documentsBrowser,
          [instanceId]: { ...instance, year },
        },
      };
    }),

  setDocumentsBrowserSortBy: (instanceId, sortBy) =>
    set((state) => {
      const instance = getOrCreateDocumentsBrowser(state, instanceId);
      const nextInstance = { ...instance, sortBy };
      return {
        documentsBrowser: {
          ...state.documentsBrowser,
          [instanceId]: {
            ...nextInstance,
            items: getFilteredDocumentsBrowserItems(nextInstance),
          },
        },
      };
    }),

  setDocumentsBrowserSortDirection: (instanceId, sortDirection) =>
    set((state) => {
      const instance = getOrCreateDocumentsBrowser(state, instanceId);
      const nextInstance = { ...instance, sortDirection };
      return {
        documentsBrowser: {
          ...state.documentsBrowser,
          [instanceId]: {
            ...nextInstance,
            items: getFilteredDocumentsBrowserItems(nextInstance),
          },
        },
      };
    }),

  listDocuments: async (instanceId, categoryId, year = 0, documentScope = "current") => {
    const state = get();
    const instance = getOrCreateDocumentsBrowser(state, instanceId);

    if (
      instance.hasLoadedItems &&
      Number(instance.categoryId ?? 0) === Number(categoryId) &&
      instance.documentScope === documentScope
    ) {
      set({
        documentsBrowser: {
          ...state.documentsBrowser,
          [instanceId]: {
            ...instance,
            documentScope,
            categoryId,
            year,
            items: getFilteredDocumentsBrowserItems({ ...instance, categoryId, year }),
            isLoading: false,
            listError: null,
          },
        },
      });
      return;
    }

    set({
      documentsBrowser: {
        ...state.documentsBrowser,
        [instanceId]: {
          ...instance,
          documentScope,
          categoryId,
          year,
          isLoading: true,
          listError: null,
        },
      },
    });

    try {
      const isLocal = getSelectedBackend() === localBackend;
      const res = isLocal
        ? listPrivateDocsLocal({ limit: 0, offset: 0, category_id: categoryId || undefined })
        : await listPrivateDocsAPI({
            limit: 0,
            offset: 0,
            category_id: categoryId || undefined,
            document_scope: documentScope,
          });
      const allItems = res.items ?? [];

      const currentState = get();
      const currentInstance = currentState.documentsBrowser[instanceId];
      if (currentInstance) {
        const nextInstance = {
          ...currentInstance,
          documentScope,
          categoryId,
          year,
          allItems,
        };
        set({
          documentsBrowser: {
            ...currentState.documentsBrowser,
            [instanceId]: {
              ...nextInstance,
              sharedScopeAvailable:
                documentScope === "shared"
                  ? Boolean(res.scopeResolved)
                  : currentInstance.sharedScopeAvailable,
              items: getFilteredDocumentsBrowserItems(nextInstance),
              hasLoadedItems: true,
              isLoading: false,
            },
          },
        });
      }
    } catch (e: any) {
      const currentState = get();
      const currentInstance = currentState.documentsBrowser[instanceId];
      if (currentInstance) {
        set({
          documentsBrowser: {
            ...currentState.documentsBrowser,
            [instanceId]: {
              ...currentInstance,
              allItems: [],
              items: [],
              hasLoadedItems: false,
              listError: e?.message ?? "Failed to load documents",
              isLoading: false,
            },
          },
        });
      }
    }
  },

  listDocumentCategories: async (instanceId, documentScope = "current") => {
    const state = get();
    const instance = getOrCreateDocumentsBrowser(state, instanceId);

    set({
      documentsBrowser: {
        ...state.documentsBrowser,
        [instanceId]: { ...instance, isLoadingCategories: true, categoriesError: null },
      },
    });

    try {
      const isLocal = getSelectedBackend() === localBackend;
      const res = isLocal
        ? listPrivateDocCategoriesLocal()
        : await listPrivateDocCategoriesAPI({
            document_scope: documentScope,
          });

      const currentState = get();
      const currentInstance = currentState.documentsBrowser[instanceId];
      if (currentInstance) {
        set({
          documentsBrowser: {
            ...currentState.documentsBrowser,
            [instanceId]: {
              ...currentInstance,
              documentScope,
              sharedScopeAvailable:
                documentScope === "shared"
                  ? Boolean(res.scopeResolved)
                  : currentInstance.sharedScopeAvailable,
              categories: res.items ?? [],
              isLoadingCategories: false,
            },
          },
        });
      }
    } catch (e: any) {
      const currentState = get();
      const currentInstance = currentState.documentsBrowser[instanceId];
      if (currentInstance) {
        set({
          documentsBrowser: {
            ...currentState.documentsBrowser,
            [instanceId]: {
              ...currentInstance,
              sharedScopeAvailable:
                documentScope === "shared" ? false : currentInstance.sharedScopeAvailable,
              categories: [],
              categoriesError: e?.message ?? "Failed to load categories",
              isLoadingCategories: false,
            },
          },
        });
      }
    }
  },

  uploadDocument: async (instanceId, file, categoryId, itemName) => {
    const state = get();
    const instance = getOrCreateDocumentsBrowser(state, instanceId);
    const safeItemName = (itemName ?? "").trim();

    set({
      documentsBrowser: {
        ...state.documentsBrowser,
        [instanceId]: { ...instance, isUploading: true, uploadError: null },
      },
    });

    try {
      const isLocal = getSelectedBackend() === localBackend;
      const uploadPayload = {
        file,
        category_id: categoryId,
        item_name: safeItemName || undefined,
      };
      const res = isLocal
        ? await uploadPrivateDocLocal(uploadPayload as any)
        : await uploadPrivateDocAPI(uploadPayload as any);

      const currentState = get();
      const currentInstance = currentState.documentsBrowser[instanceId];
      if (currentInstance) {
        const nextAllItems = [res as PrivateDocListItem, ...currentInstance.allItems];
        set({
          documentsBrowser: {
            ...currentState.documentsBrowser,
            [instanceId]: {
              ...currentInstance,
              allItems: nextAllItems,
              items: getFilteredDocumentsBrowserItems({
                ...currentInstance,
                allItems: nextAllItems,
              }),
              hasLoadedItems: true,
              isUploading: false,
              uploadError: null,
            },
          },
        });
      }
      return res as PrivateDocListItem;
    } catch (e: any) {
      const currentState = get();
      const currentInstance = currentState.documentsBrowser[instanceId];
      if (currentInstance) {
        set({
          documentsBrowser: {
            ...currentState.documentsBrowser,
            [instanceId]: {
              ...currentInstance,
              isUploading: false,
              uploadError: "Upload failed. Please try again.",
            },
          },
        });
      }
      throw e;
    }
  },

  resetDocumentsBrowser: (instanceId) =>
    set((state) => {
      const newState = { ...state.documentsBrowser };
      delete newState[instanceId];
      return { documentsBrowser: newState };
    }),
}));
